import { useState, type MouseEvent } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  ButtonBase,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import TranslateIcon from '@mui/icons-material/Translate';
import LogoutIcon from '@mui/icons-material/Logout';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { useColorMode } from '@/theme/ColorModeContext';
import { isAuthBypassEnabled } from '@/auth/authSettings';

const TABS = [
  { path: '/budget', labelKey: 'nav.budget' },
  { path: '/claims', labelKey: 'nav.claims' },
] as const;

/**
 * Application shell for the employee portal: top app bar with branding, tab
 * navigation, color mode toggle, language switcher, and account menu, with
 * the routed page rendered below via {@link Outlet}.
 *
 * @returns The portal's page layout.
 */
export function Layout() {
  const { t, i18n } = useTranslation();
  const { mode, toggleColorMode } = useColorMode();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);

  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
  const displayName = account?.name ?? account?.username ?? '';
  const initials = displayName
    .split(' ')
    .map((part) => part.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // Match by prefix (not exact path) so nested/detail routes under a tab keep it highlighted.
  const activeTab = TABS.find((tab) => location.pathname.startsWith(tab.path))?.path ?? '/budget';

  const openLang = (event: MouseEvent<HTMLElement>) => setLangAnchor(event.currentTarget);
  const closeLang = () => setLangAnchor(null);
  const chooseLanguage = (lng: 'nl' | 'en') => {
    void i18n.changeLanguage(lng);
    closeLang();
  };

  const openAccount = (event: MouseEvent<HTMLElement>) => setAccountAnchor(event.currentTarget);
  const closeAccount = () => setAccountAnchor(null);
  const signOut = () => {
    closeAccount();
    void instance.logoutRedirect();
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 2, minHeight: '72px !important', px: { xs: 2, md: 3 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mr: { xs: 0, md: 2 }, minWidth: { md: 245 } }}>
            <ButtonBase
              onClick={() => navigate('/budget')}
              aria-label={t('nav.budget')}
              sx={(theme) => ({
                display: 'grid',
                placeItems: 'center',
                width: 42,
                height: 42,
                flexShrink: 0,
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.18),
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.07),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.13) },
              })}
            >
              <Box component="img" src="/brand/allphi-logo.png" alt={t('app.logoAlt')} sx={{ width: 30, height: 30, objectFit: 'contain' }} />
            </ButtonBase>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={750} lineHeight={1.2} noWrap>{t('app.title')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, letterSpacing: '0.02em' }} noWrap>{t('app.subtitle')}</Typography>
            </Box>
          </Stack>

          <Tabs
            value={activeTab}
            onChange={(_event, value: string) => navigate(value)}
            sx={{ flexGrow: 1, minHeight: 'unset' }}
          >
            {TABS.map((tab) => (
              <Tab key={tab.path} value={tab.path} label={t(tab.labelKey)} sx={{ minHeight: 'unset' }} />
            ))}
          </Tabs>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={mode === 'dark' ? t('app.lightMode') : t('app.darkMode')}>
              <IconButton onClick={toggleColorMode} color="inherit">
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title={t('app.language')}>
              <IconButton onClick={openLang} color="inherit">
                <TranslateIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={langAnchor} open={Boolean(langAnchor)} onClose={closeLang}>
              <MenuItem selected={i18n.language === 'nl'} onClick={() => chooseLanguage('nl')}>
                {t('app.dutch')}
              </MenuItem>
              <MenuItem selected={i18n.language === 'en'} onClick={() => chooseLanguage('en')}>
                {t('app.english')}
              </MenuItem>
            </Menu>

            {!isAuthBypassEnabled && (
              <>
                <Tooltip title={t('app.accountMenu')}>
                  <IconButton onClick={openAccount} color="inherit" aria-label={t('app.accountMenu')}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>{initials || '?'}</Avatar>
                  </IconButton>
                </Tooltip>
                <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={closeAccount}>
                  {displayName && (
                    <MenuItem disabled sx={{ opacity: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {displayName}
                      </Typography>
                    </MenuItem>
                  )}
                  <MenuItem onClick={signOut}>
                    <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                    {t('app.signOut')}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3, flexGrow: 1 }}>
        <Outlet />
      </Container>
    </Box>
  );
}
